import { type KeyboardEvent, type ReactNode } from "react";
import { format, parseISO } from "date-fns";
import { Info } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n/use-translation";
import {
  formatCurrency,
  formatDateRange,
  formatNumber,
  formatPercent,
  formatWeight,
} from "@/lib/format";
import { foodWastePercent } from "@/lib/waste-report-metrics";
import { wowPercent, wastePerCoverGrams } from "@/lib/kpi";
import { useClaims, hasPermission } from "@/hooks/use-claims";
import type { Lens } from "@/hooks/use-lens";

/** Days of the selected range filled at the daily average, for the Est. chip (POS2-996). */
type Fill = { filledDays: number; rangeDays: number };

type Props = {
  totalWeight: number;
  totalCost: number;
  /** Food spend for the range — the entered sum, or the estimate once days go unentered.
   *  Null → "N/A" until a week is entered. */
  foodSpend: number | null;
  /** Spend pro-rated to the selected range (POS2-923) — card 2's % denominator only. */
  wastePctSpend: number | null;
  /** Equal-length previous-period totals for the WoW pill; null hides it. */
  previous: { totalWeight: number; totalCost: number } | null;
  /** Waste % of purchases target (percent, e.g. 6.0) from resolved config; null → no colour/notch. */
  targetPct: number | null;
  currency: string;
  /** Global £/kg lens (POS2-865). */
  lens: Lens;
  /** ISO timestamp of the latest food-spend period, for the "Entered · {date}" chip. */
  foodSpendEnteredAt: string | null;
  /** Chip label for who entered it (POS2-969): "you" | display name | null (unknown). */
  foodSpendEnteredBy: string | null;
  /** Dates the spend covers (span across periods); null until one is entered. */
  foodSpendCoveredRange: { start: string; end: string } | null;
  /** Non-null when part of the shown figure is daily-average-filled (POS2-996). */
  foodSpendFill: Fill | null;
  /** Open the Food spend weekly dialog — the only entry path (POS2-996). */
  onOpenFoodSpend: () => void;
  // Covers (POS2-921) — manual, mirrors food spend. Card 3 divides by the range figure
  // (like Waste %'s wastePctSpend), card 5 shows it.
  /** Covers for the range — entered sum or estimate; null → "N/A". */
  covers: number | null;
  /** Covers pro-rated to the selected range — card 3's denominator only (POS2-813 removes it). */
  wastePerCoverCovers: number | null;
  /** Resolved `waste_per_cover_target_g` (grams); null → no colour/notch on card 3. */
  wastePerCoverTargetG: number | null;
  /** The covers card now takes exactly what the food spend card takes.
   *
   *  It used to carry a chip of its own — ENTERED · SITE — because a range holds days from
   *  several people through two surfaces and naming one of them looked arbitrary. Naming the
   *  **most recent** writer, with the date beside them, is not arbitrary and is what every other
   *  entry card in this row already does. */
  /** ISO timestamp of the latest counted day's write, for the "Entered · {date}" chip. */
  coversEnteredAt: string | null;
  /** Who that was (POS2-969): "you" | display name | null when the id resolves to nobody. */
  coversEnteredBy: string | null;
  /** Non-null when part of the range is daily-average-filled rather than entered. */
  coversFill: { filledDays: number; rangeDays: number } | null;
  /** Open the Covers weekly dialog — the only entry path (POS2-996). */
  onOpenCovers: () => void;
  /** A time window or service keeps only part of each day (`isPartOfDay`): cards 2 and 3 then
   * withhold their ratio, since spend and covers are whole-day figures (POS2-1492). */
  partOfDay: boolean;
};

const CHIP =
  "mt-2 inline-flex w-fit max-w-full items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider";

/**
 * Green "Measured · Scrappy" (device-weighed) vs grey "Entered · {who}" (manual).
 * `by` names the entrant (POS2-969): "you" when the viewer entered it, their display
 * name otherwise, null when unknown — then the chip shows just the date.
 */
function ProvChip({
  kind,
  by,
  date,
}: {
  kind: "measured" | "entered";
  by?: string | null;
  date?: string | null;
}) {
  const { t } = useTranslation();
  const measured = kind === "measured";
  const parts = [by, date].filter(Boolean);
  const text = measured
    ? t("dashboard.kpiMeasured")
    : parts.length
      ? t("dashboard.kpiEnteredBy", { who: parts.join(", ") })
      : t("dashboard.kpiEntered");
  return (
    <span
      className={cn(CHIP, measured ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500")}
    >
      {/* The entrant can be a full email address (POS2-1068) — ellipsise rather than let the
          pill outgrow its card, keeping the whole label on the title. */}
      <span className="min-w-0 truncate" title={text}>
        {text}
      </span>
    </span>
  );
}

/**
 * Part-entered, part-derived figure (POS2-996): the range holds days no entered week
 * covers, filled at the daily average across the entered weeks. Named so the reader
 * knows how much of the number nobody typed.
 *
 * Counts filled days against the whole range, so under the gaps-only rule the two need
 * not add up: days beyond the entered weeks are neither entered nor filled. Naming those
 * separately was tried and dropped — one format in every state reads better on a 9px chip.
 */
function EstChip({ filledDays, rangeDays }: Fill) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(CHIP, "whitespace-nowrap bg-orange-50 text-orange-700")}
      data-testid="dash-kpi-est-chip"
    >
      {t("dashboard.kpiEstimated", { filled: filledDays, total: rangeDays })}
    </span>
  );
}

const CARD =
  "relative flex flex-col rounded-xl border border-gray-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(17,24,39,0.04),0_1px_3px_rgba(17,24,39,0.06)]";

/**
 * Homepage KPI row (POS2-869 + POS2-921, design §4). The full five-card row:
 * Food waste (with a week-over-week pill), Waste % of purchases, Waste per cover,
 * Food spend, and Covers. The last two are the manually-entered figures: each opens
 * its weekly dialog and nothing else (POS2-996). **Covers no longer mirrors Food spend**: since
 * POS2-1259 CP5 it reads the occupancy store per day, so its provenance is site-level rather
 * than per-week and its write permission is `occupancy:update`, not `waste:update`.
 */
export function DashboardKpis({
  totalWeight,
  totalCost,
  foodSpend,
  wastePctSpend,
  previous,
  targetPct,
  currency,
  lens,
  foodSpendEnteredAt,
  foodSpendCoveredRange,
  foodSpendFill,
  onOpenFoodSpend,
  foodSpendEnteredBy,
  covers,
  wastePerCoverCovers,
  wastePerCoverTargetG,
  coversEnteredAt,
  coversEnteredBy,
  coversFill,
  onOpenCovers,
  partOfDay,
}: Props) {
  const { locale, t } = useTranslation();
  // Offering a dialog the save would 403 is the defect POS2-1211 fixed, so each card asks for
  // the permission its own write actually needs. **They are no longer the same one**: food spend
  // still writes through `food_spend_periods.py` on `waste:update`, while covers now writes
  // occupancy overrides on `occupancy:update` (POS2-1259 decision 7). Staff holds neither, and
  // both figures still render — Staff holds the reads, and card 3 divides by covers.
  const { isPlatformAdmin, permissions } = useClaims();
  const canEnterFoodSpend = isPlatformAdmin || hasPermission(permissions, "waste:update");
  const canEnterCovers = isPlatformAdmin || hasPermission(permissions, "occupancy:update");
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <FoodWasteCard
        totalWeight={totalWeight}
        totalCost={totalCost}
        previous={previous}
        lens={lens}
        currency={currency}
      />
      <WastePctCard
        totalCost={totalCost}
        spend={wastePctSpend}
        targetPct={targetPct}
        partOfDay={partOfDay}
      />
      <WastePerCoverCard
        totalWeight={totalWeight}
        covers={wastePerCoverCovers}
        targetG={wastePerCoverTargetG}
        partOfDay={partOfDay}
      />
      <EntryCard
        label={t("dashboard.kpiFoodSpend")}
        value={foodSpend == null ? "—" : formatCurrency(locale, foodSpend, currency)}
        enteredAt={foodSpendEnteredAt}
        enteredBy={foodSpendEnteredBy}
        coveredRange={foodSpendCoveredRange}
        fill={foodSpendFill}
        onOpen={canEnterFoodSpend ? onOpenFoodSpend : null}
        testId="dash-kpi-food-spend"
      />
      <EntryCard
        label={t("dashboard.kpiCovers")}
        // Rounded: a daily-average-filled figure is fractional, and there is no such
        // thing as a third of a cover.
        value={covers == null ? "—" : formatNumber(locale, Math.round(covers))}
        enteredAt={coversEnteredAt}
        enteredBy={coversEnteredBy}
        coveredRange={null}
        fill={coversFill}
        onOpen={canEnterCovers ? onOpenCovers : null}
        testId="dash-kpi-covers"
      />
    </div>
  );
}

function FoodWasteCard({
  totalWeight,
  totalCost,
  previous,
  lens,
  currency,
}: {
  totalWeight: number;
  totalCost: number;
  previous: { totalWeight: number; totalCost: number } | null;
  lens: Lens;
  currency: string;
}) {
  const { locale, t } = useTranslation();
  const value =
    lens === "cost"
      ? formatCurrency(locale, totalCost, currency)
      : formatWeight(locale, totalWeight);
  const current = lens === "cost" ? totalCost : totalWeight;
  const prev = previous ? (lens === "cost" ? previous.totalCost : previous.totalWeight) : null;
  const wow = prev == null ? null : wowPercent(current, prev);

  return (
    <div className={CARD} data-testid="dash-kpi-food-waste">
      <p className="text-xs text-gray-500">{t("dashboard.kpiFoodWaste")}</p>
      <p className="mt-1 truncate font-display text-2xl font-extrabold tracking-tight tabular-nums text-gray-900">
        {value}
      </p>
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-400">
        <WowPill wow={wow} />
        {wow != null && <span>{t("dashboard.kpiVsLastPeriod")}</span>}
      </div>
      <ProvChip kind="measured" />
    </div>
  );
}

/** ▲ up (more waste, bad/red) · ▼ down (good/green) · ▬ flat, inside a 0.5% deadband. */
function WowPill({ wow }: { wow: number | null }) {
  const { locale, t } = useTranslation();
  if (wow == null) return null;
  // `wow` is already a percentage, so it is divided back to a rate: `formatPercent` owns the sign
  // and the space German puts before it, which a hand-appended "%" cannot know about.
  const tone =
    wow > 0.5
      ? { cls: "bg-red-50 text-red-700", text: `▲ ${formatPercent(locale, wow / 100)}` }
      : wow < -0.5
        ? {
            cls: "bg-green-50 text-green-700",
            text: `▼ ${formatPercent(locale, Math.abs(wow) / 100)}`,
          }
        : { cls: "bg-slate-100 text-slate-500", text: t("dashboard.kpiFlat") };
  return (
    <span
      className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-bold", tone.cls)}
      data-testid="dash-kpi-wow"
    >
      {tone.text}
    </span>
  );
}

function WastePctCard({
  totalCost,
  spend,
  targetPct,
  partOfDay,
}: {
  totalCost: number;
  /** Pro-rated to the selected range (POS2-923), not the entered period total. */
  spend: number | null;
  targetPct: number | null;
  partOfDay: boolean;
}) {
  const { t } = useTranslation();
  const rate = partOfDay ? null : foodWastePercent(totalCost, spend);
  const pct = rate == null ? null : rate * 100;

  return (
    <div className={CARD} data-testid="dash-kpi-waste-pct">
      <div className="flex items-center gap-1">
        <p className="text-xs text-gray-500">{t("dashboard.kpiWastePct")}</p>
        {pct == null && !partOfDay && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-gray-300 hover:text-gray-500"
                aria-label={t("dashboard.kpiWastePctInfo")}
              >
                <Info className="size-3" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-56">{t("dashboard.kpiWastePctHint")}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {pct == null ? (
        <p className="mt-1 font-display text-2xl font-extrabold tracking-tight tabular-nums text-gray-900">
          {t("dashboard.kpiNotAvailable")}
        </p>
      ) : (
        <PctBody pct={pct} targetPct={targetPct} />
      )}
      {partOfDay && <PartOfDayNote />}
      <ProvChip kind="measured" />
    </div>
  );
}

function PctBody({ pct, targetPct }: { pct: number; targetPct: number | null }) {
  const { locale, t } = useTranslation();
  const over = targetPct != null && pct > targetPct;
  const colour = targetPct == null ? "text-gray-900" : over ? "text-red-600" : "text-green-600";

  return (
    <>
      <p
        className={cn(
          "mt-1 font-display text-2xl font-extrabold tracking-tight tabular-nums",
          colour,
        )}
      >
        {formatPercent(locale, pct / 100)}
      </p>
      {targetPct != null && <TargetBar pct={pct} targetPct={targetPct} over={over} />}
      {targetPct != null && (
        <p className="mt-1.5 text-[11px] text-gray-400">
          {t("dashboard.kpiTarget", { target: formatPercent(locale, targetPct / 100, 0) })}
        </p>
      )}
    </>
  );
}

/** Why a ratio card shows N/A under a time or service slice (POS2-1492). Wraps, and breaks a long
 * German compound rather than overflow the narrowest five-across card. */
function PartOfDayNote() {
  const { t } = useTranslation();
  return (
    <p
      className="mt-1.5 text-[11px] leading-snug break-words text-gray-400"
      data-testid="dash-kpi-part-of-day"
    >
      {t("dashboard.kpiPartOfDay")}
    </p>
  );
}

/** Fill scaled so the target notch sits comfortably left of a max headroom. */
function TargetBar({ pct, targetPct, over }: { pct: number; targetPct: number; over: boolean }) {
  const scale = Math.max(pct, targetPct * 1.6);
  const fillPct = Math.min(100, (pct / scale) * 100);
  const notchPct = (targetPct / scale) * 100;
  return (
    <div className="relative mt-2 h-[5px] overflow-hidden rounded-full bg-gray-100">
      <span
        className={cn(
          "absolute inset-y-0 left-0 rounded-full",
          over ? "bg-red-600" : "bg-green-600",
        )}
        style={{ width: `${fillPct}%` }}
      />
      <span
        className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-gray-900/50"
        style={{ left: `${notchPct}%` }}
      />
    </div>
  );
}

/** Card 3 (design §4): waste grams ÷ covers, red/green + target notch vs waste_per_cover_target_g. */
function WastePerCoverCard({
  totalWeight,
  covers,
  targetG,
  partOfDay,
}: {
  totalWeight: number;
  covers: number | null;
  targetG: number | null;
  partOfDay: boolean;
}) {
  const { t } = useTranslation();
  const grams = partOfDay ? null : wastePerCoverGrams(totalWeight, covers);

  return (
    <div className={CARD} data-testid="dash-kpi-waste-per-cover">
      <div className="flex items-center gap-1">
        <p className="text-xs text-gray-500">{t("dashboard.kpiWastePerCover")}</p>
        {grams == null && !partOfDay && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-gray-300 hover:text-gray-500"
                aria-label={t("dashboard.kpiWastePerCoverInfo")}
              >
                <Info className="size-3" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-56">
              {t("dashboard.kpiWastePerCoverHint")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {grams == null ? (
        <p className="mt-1 font-display text-2xl font-extrabold tracking-tight tabular-nums text-gray-900">
          {t("dashboard.kpiNotAvailable")}
        </p>
      ) : (
        <PerCoverBody grams={grams} targetG={targetG} />
      )}
      {partOfDay && <PartOfDayNote />}
      <ProvChip kind="measured" />
    </div>
  );
}

function PerCoverBody({ grams, targetG }: { grams: number; targetG: number | null }) {
  const { locale, t } = useTranslation();
  const over = targetG != null && grams > targetG;
  const colour = targetG == null ? "text-gray-900" : over ? "text-red-600" : "text-green-600";

  return (
    <>
      <p
        className={cn(
          "mt-1 font-display text-2xl font-extrabold tracking-tight tabular-nums",
          colour,
        )}
      >
        {t("dashboard.kpiGrams", { value: formatNumber(locale, Math.round(grams)) })}
      </p>
      {targetG != null && <TargetBar pct={grams} targetPct={targetG} over={over} />}
      {targetG != null && (
        <p className="mt-1.5 text-[11px] text-gray-400">
          {t("dashboard.kpiGoodPractice", { target: `${formatNumber(locale, targetG)}g` })}
        </p>
      )}
    </>
  );
}

/**
 * Cards 4 and 5 (design §4): the two manually-entered figures. Since POS2-996 the card is
 * a pure entry point — clicking or Enter/Space opens its weekly dialog, in every state,
 * and there is no type-in-place path. The blue "✎ Editable" treatment is unchanged from
 * the inline era, so the affordance still reads as the editable box it always was.
 */
function EntryCard({
  label,
  value,
  enteredAt,
  enteredBy,
  coveredRange,
  fill,
  provenance: provenanceOverride,
  onOpen,
  testId,
}: {
  label: string;
  /** Pre-formatted figure (currency or count), or "—" when nothing is entered. */
  value: string;
  enteredAt: string | null;
  enteredBy: string | null;
  coveredRange: { start: string; end: string } | null;
  fill: Fill | null;
  /** Replaces the entered/estimated chips entirely. Covers passes its site-level state here;
   *  food spend passes nothing and keeps the per-week provenance it still has. */
  provenance?: ReactNode;
  /** Null for a caller without `waste:update` — the card then reads as a plain figure
   *  (POS2-1211). Every affordance below is an entry path, and entry is refused with a 403. */
  onOpen: (() => void) | null;
  testId: string;
}) {
  const { locale, t } = useTranslation();
  const enteredDate = enteredAt ? format(parseISO(enteredAt), "d MMM") : null;
  const covered = coveredRange
    ? formatDateRange(locale, coveredRange.start, coveredRange.end)
    : null;
  const provenance =
    provenanceOverride ??
    (fill ? (
      <EstChip {...fill} />
    ) : (
      enteredDate && <ProvChip kind="entered" by={enteredBy} date={enteredDate} />
    ));

  // Read-only: the same figure with the chrome of the three measured cards, so the row
  // reads as five KPIs rather than two broken buttons. Both sub-labels are instructions
  // ("manage weeks", "click to add a week") and neither applies, so the line is the bare
  // covered period or nothing.
  if (!onOpen) {
    return (
      <div className={CARD} data-testid={testId}>
        <p className="text-xs text-gray-500">{t("dashboard.kpiEntryLabel", { label })}</p>
        {/* The figure gets its own hook: the card also holds a provenance chip whose text
            contains digits ("Est. · 1 of 7 days filled"), so anything reading the card's text
            wholesale can pick up the wrong number (POS2-1259 CP5). */}
        <p
          className="mt-1 truncate font-display text-2xl font-extrabold tracking-tight tabular-nums text-gray-900"
          data-testid={`${testId}-value`}
        >
          {value}
        </p>
        {covered && <p className="mt-1.5 text-[11px] text-gray-400">{covered}</p>}
        {provenance}
      </div>
    );
  }

  return (
    <div
      className={cn(
        CARD,
        "cursor-pointer border-[1.5px] border-dashed border-blue-500 bg-gradient-to-b from-white to-blue-50/60 outline-none hover:border-solid focus-visible:ring-1 focus-visible:ring-blue-500",
      )}
      data-testid={testId}
      role="button"
      tabIndex={0}
      aria-label={t("dashboard.kpiManageWeeks", { label })}
      onClick={onOpen}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="absolute -top-2 right-2.5 rounded-full bg-blue-500 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white">
        ✎ {t("dashboard.kpiEditable")}
      </span>
      <p className="text-xs font-semibold text-blue-600">
        {t("dashboard.kpiEntryLabel", { label })}
      </p>
      <p
        className="mt-1 truncate font-display text-2xl font-extrabold tracking-tight tabular-nums text-gray-900"
        data-testid={`${testId}-value`}
      >
        {value}
      </p>
      <p className="mt-1.5 text-[11px] text-gray-400">
        {covered ? t("dashboard.kpiCovered", { covered }) : t("dashboard.kpiAddWeek")}
      </p>
      {provenance}
    </div>
  );
}
